import React, { useState } from 'react';
import SimpleModal from './SimpleModal';
import Button from './Button';
import Input from './Input';
import { KiddiFitWordmark } from './Brand';
import {
  signInWithEmail,
  registerWithEmail,
  resetPassword,
  signInWithGoogle,
} from '../firebase';
import { createUserProfile } from '../firebase';
import './AuthModal.css';

const AuthModal = ({ isOpen, onClose, onSuccess, initialMode = 'login' }) => {
  const [mode, setMode] = useState(initialMode); // 'login', 'register', 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const clearForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    clearForm();
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const user = await signInWithGoogle();
      await createUserProfile(user.uid, {
        displayName: user.displayName,
        email: user.email,
      });
      onSuccess(user);
    } catch (error) {
      setError('Failed to login with Google. Please try again.');
      console.error('Google login error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async () => {
    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      console.log('Attempting to login with email:', email);
      const user = await signInWithEmail(email, password);
      console.log('Login successful, user ID:', user.uid);
      onSuccess(user);
    } catch (error) {
      console.error('Email login error:', error);
      if (error.code === 'auth/user-not-found') {
        setError('No account found with this email. Please register first.');
      } else if (error.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (error.code === 'auth/invalid-credential') {
        setError(
          'Invalid email or password. Please check your credentials and try again.'
        );
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else {
        setError(`Login failed: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!email || !password || !confirmPassword) {
      setError('Please fill in all fields.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      console.log('Attempting to register with email:', email);
      const user = await registerWithEmail(email, password);
      console.log('Registration successful, user ID:', user.uid);

      await createUserProfile(user.uid, {
        displayName: email.split('@')[0], // Use email prefix as display name
        email: user.email,
      });
      console.log('User profile created successfully');

      onSuccess(user);
    } catch (error) {
      console.error('Registration error:', error);
      if (error.code === 'auth/email-already-in-use') {
        setError(
          'An account with this email already exists. Please login instead.'
        );
      } else if (error.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (error.code === 'auth/weak-password') {
        setError('Password is too weak. Please choose a stronger password.');
      } else if (error.code === 'auth/invalid-credential') {
        setError('Invalid email or password format.');
      } else {
        setError(`Registration failed: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await resetPassword(email);
      setSuccess('Password reset email sent! Check your inbox.');
    } catch (error) {
      if (error.code === 'auth/user-not-found') {
        setError('No account found with this email address.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else {
        setError('Failed to send reset email. Please try again.');
      }
      console.error('Password reset error:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderLoginForm = () => (
    <div className='auth-form'>
      <div className='auth-logo'>
        <KiddiFitWordmark />
      </div>
      <button
        className='google-login-btn'
        onClick={handleGoogleLogin}
        disabled={loading}
      >
        <svg className='google-icon' viewBox='0 0 24 24'>
          <path
            fill='#4285F4'
            d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
          />
          <path
            fill='#34A853'
            d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
          />
          <path
            fill='#FBBC05'
            d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
          />
          <path
            fill='#EA4335'
            d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
          />
        </svg>
        Continue with Google
      </button>
      <div className='auth-divider'>
        <span>or</span>
      </div>
      <Input
        type='email'
        placeholder='Email'
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        disabled={loading}
      />
      <Input
        type='password'
        placeholder='Password'
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        disabled={loading}
      />
      {error && <div className='auth-error'>{error}</div>}
      <div className='auth-actions'>
        <Button
          type='primary'
          onClick={handleEmailLogin}
          disabled={loading}
          style={{ width: '100%' }}
        >
          {loading ? 'Signing in...' : 'Login'}
        </Button>
      </div>
      <div className='auth-links'>
        <button
          className='auth-link'
          onClick={() => handleModeChange('forgot')}
          disabled={loading}
        >
          Forgot Password?
        </button>
        <button
          className='auth-link'
          onClick={() => handleModeChange('register')}
          disabled={loading}
        >
          Create Account
        </button>
      </div>
    </div>
  );

  const renderRegisterForm = () => (
    <div className='auth-form'>
      <div className='auth-logo'>
        <KiddiFitWordmark />
      </div>
      <button
        className='google-login-btn'
        onClick={handleGoogleLogin}
        disabled={loading}
      >
        <svg className='google-icon' viewBox='0 0 24 24'>
          <path
            fill='#4285F4'
            d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
          />
          <path
            fill='#34A853'
            d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
          />
          <path
            fill='#FBBC05'
            d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
          />
          <path
            fill='#EA4335'
            d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
          />
        </svg>
        Continue with Google
      </button>
      <div className='auth-divider'>
        <span>or</span>
      </div>
      <Input
        type='email'
        placeholder='Email'
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        disabled={loading}
      />
      <Input
        type='password'
        placeholder='Password'
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        disabled={loading}
      />
      <Input
        type='password'
        placeholder='Confirm Password'
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        disabled={loading}
      />
      {error && <div className='auth-error'>{error}</div>}
      <div className='auth-actions'>
        <Button
          type='primary'
          onClick={handleRegister}
          disabled={loading}
          style={{ width: '100%' }}
        >
          {loading ? 'Creating Account...' : 'Create Account'}
        </Button>
      </div>
      <div className='auth-links'>
        <button
          className='auth-link'
          onClick={() => handleModeChange('login')}
          disabled={loading}
        >
          Login
        </button>
      </div>
    </div>
  );

  const renderForgotPasswordForm = () => (
    <div className='auth-form'>
      <div className='auth-logo'>
        <KiddiFitWordmark />
      </div>
      <Input
        type='email'
        placeholder='Email'
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        disabled={loading}
      />
      {error && <div className='auth-error'>{error}</div>}
      {success && <div className='auth-success'>{success}</div>}
      <div className='auth-actions'>
        <Button
          type='primary'
          onClick={handleForgotPassword}
          disabled={loading}
          style={{ width: '100%', marginBottom: '10px' }}
        >
          {loading ? 'Sending...' : 'Send Reset Email'}
        </Button>
      </div>
      <div className='auth-links'>
        <button
          className='auth-link'
          onClick={() => handleModeChange('login')}
          disabled={loading}
        >
          Back to Login
        </button>
      </div>
    </div>
  );

  const getTitle = () => {
    switch (mode) {
      case 'register':
        return 'Create Account';
      case 'forgot':
        return 'Reset Password';
      default:
        return 'Login';
    }
  };

  const getBody = () => {
    switch (mode) {
      case 'register':
        return renderRegisterForm();
      case 'forgot':
        return renderForgotPasswordForm();
      default:
        return renderLoginForm();
    }
  };

  return (
    <SimpleModal
      isOpen={isOpen}
      onClose={onClose}
      title={getTitle()}
      body={getBody()}
    />
  );
};

export default AuthModal;
